import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import ZoomableImage from "./ZoomableImage";
import ShowcaseVisual, { ratioValue } from "./ShowcaseVisual";
import { useWideStage } from "./VisualLayer";

// The one media dialog in the portfolio (Figma node 6653:7059). Homepage
// galleries, card media, UI-card galleries and article figures all open this,
// so enlarging an image works the same everywhere:
//
//   - the artwork is the dialog surface, carrying the dialog radius
//   - a ghost X in the top-right corner closes it
//   - with more than one item, a glass capsule under the artwork holds the
//     count and the previous / next arrows — the same capsule the homepage
//     galleries carry — so a single image opens as a plain preview
//
// An item is an image, a clip, or a `visual`: a stage composition (screens
// over a backdrop, as on the homepage) drawn at the largest size that fits.
//
// The gallery is a run with two ends rather than a loop: the arrow that would
// leave the run is disabled.
//
// Clips play here exactly as they do on the card — looping, no controls — so
// opening one only makes it bigger.
//
// Artwork in the dialog is zoomable with a mouse: see ZoomableImage. It owns
// the pointer over the image or clip, which is why the swipe below stands aside
// while something is zoomed. On a touch screen the artwork is plain and the
// browser's own pinch does the zooming.
//
// Keyboard: Escape closes, Left / Right step through the gallery.
// Pointer: click the artwork to zoom, drag it to pan while zoomed.
// Touch: horizontal swipe steps through the gallery; pinch zooms the page.

const SWIPE_THRESHOLD = 48;

// A gallery often opens on a screen its card does not show. Whatever opens a
// gallery warms its first item on hover, focus or press, so by the click the
// file is already downloaded. Warming fetches and never decodes: Chromium
// queues `decode()` requests with the decodes the next frame is waiting on,
// so decoding a 3000px neighbour as the dialog opened held its first frames
// back by a tenth of a second. Painting decodes off the main thread anyway.
const warmed = new Set();
export function warmMedia(item) {
  if (!item || !item.src || item.type === "video" || warmed.has(item.src)) return;
  warmed.add(item.src);
  const image = new Image();
  image.onerror = () => warmed.delete(item.src);
  image.src = item.src;
}

// Button/Ghost, large icon: no surface of its own, so it reads as chrome on top
// of the dimmed page rather than as a control on a surface.
const ghostIconButtonBase =
  "inline-flex items-center justify-center transition-colors duration-200 " +
  "rounded-[var(--button-large-icon-radius)] " +
  "text-[color:var(--color-icon-inverse)] " +
  "hover:bg-[color:var(--white-50-opacity-10)] " +
  "focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-[color:var(--color-stroke-focus)] " +
  "disabled:opacity-30 disabled:hover:bg-transparent";

// A zoomed image fills the window, so the chrome that was sitting on the dimmed
// page ends up on the artwork — a near-white icon on what is often a near-white
// screenshot. Hand it back the surface it was drawn against: the dialog's own
// scrim, blurred over whatever it now covers. Nothing moves and nothing changes
// behaviour; the controls just stay legible on any image.
const chromeOverArtwork =
  "group-has-[[data-zoomed=true]]:bg-[color:var(--dialog-background-overlay)] " +
  "group-has-[[data-zoomed=true]]:backdrop-blur-md";

const ghostIconButtonClass = `${ghostIconButtonBase} ${chromeOverArtwork} p-[var(--button-large-icon-paddingVertical)]`;

// The artwork's corner. The 40px dialog radius is drawn for a 1216px frame; on
// a 358px phone frame it rounds off a visible part of the screenshot, so the
// corner steps down with the frame — 16 on a phone, 24 on a tablet.
const artworkClass =
  "max-h-full max-w-full object-contain " +
  "rounded-[var(--radius-200)] sm:rounded-[var(--radius-250)] lg:rounded-[var(--radius-350)]";

// Opening is two layers, each animating one property. The scrim carries the
// blur at its full radius from the first frame and only fades in: Chromium
// does not interpolate a backdrop-filter radius smoothly, so easing the blur
// itself made the page jump from sharp to soft in a single frame. The
// contents follow a beat later and arrive once, faded and very slightly
// scaled, so the dimmed page is already behind them; the first artwork does
// not fade again on its own layer, which had left it half transparent over
// the page (on an article, two copies of the same figure at once).
const scrimMotion = {
  hidden: { opacity: 0, transition: { duration: 0.16, ease: "easeIn" } },
  shown: { opacity: 1, transition: { duration: 0.18, ease: "easeOut" } },
};
const contentMotion = {
  hidden: { opacity: 0, scale: 0.98, transition: { duration: 0.14, ease: "easeIn" } },
  shown: { opacity: 1, scale: 1, transition: { duration: 0.22, ease: [0.2, 0, 0, 1], delay: 0.04 } },
};
// Every layer that fades out is driven from script. framer-motion hands an
// opacity animation to the browser, and when that ends it cancels it a frame
// before writing the final value, so a layer fading to 0 showed at full
// opacity for one frame before it was removed: the dialog flashed back on as
// it closed, and the outgoing screen as a gallery stepped. An `onUpdate`
// listener is what keeps an animation in script.
const inScript = () => {};

const scrimStyle = {
  background: "var(--dialog-background-overlay)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
};

// A stage composition at the largest size the dialog's stage holds whole: the
// fit box is a size container, so the width is the smaller of its width and
// its height times the ratio the stage is currently drawn at. A `bare`
// composition is the screens alone, with no stage behind them. A screen with
// a clip plays it, looping, unless the reader prefers reduced motion.
function FittedVisual({ visual, alt }) {
  const wide = useWideStage();
  const reduced = useReducedMotion();
  const ratio = wide ? visual.ratio : visual.narrowRatio || visual.ratio;
  return (
    <div className="pf-lightbox__fit">
      <div
        className="pf-lightbox__visual"
        style={{ "--pf-fit-ratio": ratioValue(ratio) }}
        onClick={(event) => event.stopPropagation()}
      >
        <ShowcaseVisual
          project={{ id: "", href: "", title: "" }}
          visual={{ ...visual, alt: alt || visual.alt }}
          linked={false}
          priority
          motion={!reduced}
          className={visual.bare ? "pf-visual--bare" : ""}
        />
      </div>
    </div>
  );
}

function MediaLightbox({ open, items = [], title = "", initialIndex = 0, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(0);
  const closeButtonRef = useRef(null);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const touchStartX = useRef(null);
  const count = items.length;
  const hasMany = count > 1;
  const atStart = index === 0;
  const atEnd = index === count - 1;

  // The run has two ends: a step that would leave it is simply not taken. Kept
  // free of `index` so its identity only changes with the gallery itself —
  // the effects below depend on it, and must not re-run on every step.
  const step = useCallback(
    (delta) => {
      setDirection(delta);
      setIndex((current) => {
        const target = current + delta;
        return target < 0 || target > count - 1 ? current : target;
      });
    },
    [count]
  );

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  // Hold the neighbours in cache so stepping shows the next screen rather than
  // an empty frame that fills in a moment later.
  useEffect(() => {
    if (!open) return;
    [items[index - 1], items[index + 1]].forEach(warmMedia);
  }, [open, items, index]);

  // Lock the page behind the viewer and restore focus to the card on close. This
  // hangs on `open` alone: it must run when the dialog opens and closes, and at
  // no other moment, or stepping through a gallery would keep pulling focus back
  // to the close button. Hiding the overflow takes a classic scrollbar away, so
  // the body is padded by its width or the page would widen under the opening
  // dialog. Chromium does not keep a root `scrollbar-gutter` once overflow is
  // hidden.
  useEffect(() => {
    if (!open) return undefined;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const previouslyFocused = document.activeElement;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, step]);

  // Reaching an end disables the arrow that took you there, and a disabled button
  // cannot hold focus — the browser drops it to the document. Hand focus to the
  // arrow that is still live, so the keyboard stays inside the dialog. Only when
  // focus was genuinely dropped: otherwise this would steal it from the close
  // button or from wherever the reader put it.
  useEffect(() => {
    if (!open || !hasMany) return;
    if (document.activeElement !== document.body) return;
    if (atStart) nextButtonRef.current?.focus();
    else if (atEnd) prevButtonRef.current?.focus();
  }, [open, hasMany, atStart, atEnd]);

  if (typeof document === "undefined") return null;

  const current = items[index];
  const plate = {
    background:
      current?.background || "linear-gradient(180deg, var(--container-featured-gradient))",
  };

  return createPortal(
    <AnimatePresence>
      {open && current && (
        <motion.div
          className="group fixed inset-0 z-50"
          initial="hidden"
          animate="shown"
          exit="hidden"
          role="dialog"
          aria-modal="true"
          aria-label={title || "Media preview"}
          onClick={onClose}
          onTouchStart={(event) => {
            // A zoomed image takes the drag for panning, so the same drag must
            // not also change screen. Nor must a pinch: on a touch screen the
            // artwork zooms with the browser's own pinch (see ZoomableImage), and
            // a second finger landing cancels the swipe so letting go of the
            // pinch does not step the gallery.
            if (event.touches.length > 1 || event.target.closest?.('[data-zoomed="true"]')) {
              touchStartX.current = null;
              return;
            }
            touchStartX.current = event.touches[0].clientX;
          }}
          onTouchEnd={(event) => {
            if (touchStartX.current === null) return;
            const delta = event.changedTouches[0].clientX - touchStartX.current;
            if (Math.abs(delta) > SWIPE_THRESHOLD) step(delta < 0 ? 1 : -1);
            touchStartX.current = null;
          }}
        >
          <motion.div
            aria-hidden="true"
            className="absolute inset-0"
            style={scrimStyle}
            variants={scrimMotion}
            onUpdate={inScript}
          />
          <motion.div className="relative flex h-full flex-col" variants={contentMotion} onUpdate={inScript}>
          <button
            type="button"
            aria-label="Close preview"
            onClick={onClose}
            ref={closeButtonRef}
            className={`${ghostIconButtonClass} absolute right-2 top-2 z-10 sm:right-[var(--size-40)] sm:top-[var(--size-40)]`}
          >
            <X size={24} strokeWidth={1.5} aria-hidden="true" />
          </button>

          {/* The stage is the window between the close button and the capsule.
              A zoomed image grows past it, and is clipped by it. */}
          <div
            className={`relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-4 pt-10 sm:px-16 sm:pt-16 ${
              hasMany ? "pb-4 sm:pb-6" : "pb-10 sm:pb-16"
            }`}
          >
            <AnimatePresence mode="wait" custom={direction} initial={false}>
              <motion.div
                key={index}
                className="flex h-full w-full max-w-[1216px] items-center justify-center"
                initial={{ opacity: 0, x: direction * 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -32 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                onUpdate={inScript}
              >
                {/* The artwork is measured against the stage, not the window, so
                    a tall screenshot on a short screen shrinks to fit whole
                    rather than losing its top and bottom to the chrome. The
                    plate rides on the media box itself — artwork with an alpha
                    channel keeps an opaque surface, and no gradient shows past
                    the edges of an opaque screenshot. */}
                {current.type === "visual" ? (
                  <FittedVisual visual={current.visual} alt={current.alt} />
                ) : (
                  <ZoomableImage
                    type={current.type === "video" ? "video" : "image"}
                    src={current.src}
                    alt={current.alt || title}
                    className={artworkClass}
                    style={plate}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {hasMany && (
            <div className="relative z-10 flex justify-center px-4 pb-4 sm:pb-[var(--size-40)]">
              <div className="pf-lightbox__bar" onClick={(event) => event.stopPropagation()}>
                <p className="pf-gallery__count" aria-live="polite">
                  <span className="pf-visually-hidden">Image </span>
                  {index + 1}
                  <span aria-hidden="true"> / </span>
                  <span className="pf-visually-hidden"> of </span>
                  {count}
                </p>
                <button
                  type="button"
                  className="pf-gallery__button"
                  aria-label="Previous image"
                  ref={prevButtonRef}
                  disabled={atStart}
                  onClick={() => step(-1)}
                >
                  <ChevronLeft size={16} strokeWidth={1.75} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="pf-gallery__button"
                  aria-label="Next image"
                  ref={nextButtonRef}
                  disabled={atEnd}
                  onClick={() => step(1)}
                >
                  <ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default MediaLightbox;
